import type { Editor } from "@tiptap/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
    type Layout,
    type LayoutChangedMeta,
    useDefaultLayout,
    usePanelRef,
} from "react-resizable-panels";
import type { AgentSkillRevision } from "@common/schemas/domains/agentSkills/agent-skill.schema";
import { OrganizationAction } from "@common/userOrganizationRoles";
import {
    MainInterface,
    type MainInterfaceProps,
} from "@components/mainInterface/MainInterface";
import type { Breadcrumb } from "@components/mainInterface/MainInterfaceHeader";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { HStack } from "@design-system/HStack/HStack";
import { Loader } from "@design-system/Loader/Loader";
import { hasOpenMenu } from "@design-system/Menu/hasOpenMenu";
import { useHasOpenModal } from "@design-system/overlay/openModalLock";
import { PageSection } from "@design-system/PageSection/PageSection";
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from "@design-system/ResizablePanels/ResizablePanels";
import { Separator } from "@design-system/Separator/Separator";
import { useTemporarySideNavMode } from "@design-system/SideNav/SideNavProvider";
import { Tag } from "@design-system/Tag/Tag";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import { useCanPerformOrganizationAction } from "@hooks/permissions/organization/useCanPerformOrganizationAction";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useAgentSkill } from "@api/agentSkill.api";
import { useOrganizationClients } from "@components/organizationView/activity/useOrganizationClients";
import {
    TaskCloseButton,
    TaskPager,
    TaskPaneToggleButton,
} from "@components/tasks/TaskBody/TaskBodyHeaderControls";
import { AgentSkillBodyField } from "./AgentSkillBodyField";
import { AgentSkillDocumentHeader } from "./AgentSkillDocumentHeader";
import { AgentSkillEditActions } from "./AgentSkillEditActions";
import { AgentSkillReadActions } from "./AgentSkillReadActions";
import type { AgentSkillPanelTab } from "./agentSkillChat";
import { SAVE_FAILURE_MESSAGE } from "./agentSkillCopy";
import {
    AgentSkillEditorForm,
    type AgentSkillEditorFormHandle,
} from "./AgentSkillEditorForm";
import {
    AgentSkillFormValues,
    type DirtyRevision,
    getEditAgentSkillForm,
    pinDirtyRevision,
} from "./agentSkillForm";
import { getSkillPagerState, readSkillIdsFromState } from "./agentSkillPager";
import {
    AgentSkillSidePanel,
    type AgentSkillMetadataPatch,
} from "./AgentSkillSidePanel";
import {
    getAgentSkillErrorMessage,
    useAgentSkillLifecycle,
    useAgentSkillUpdate,
} from "./useAgentSkillLifecycle";
import styles from "./AgentSkills.module.scss";

const SKILLS_PATH = "/automation/skills";
const PANE_ID = "agent-skill-pane";
const PANE_MIN_SIZE = "340px";

export const agentSkillPagePath = (skillId: string) =>
    `${SKILLS_PATH}/${skillId}`;

/** The skill page's frame: the trail ends with the loaded skill's title. */
export const AgentSkillMainInterface = (
    props: Omit<
        MainInterfaceProps,
        "breadcrumbs" | "contained" | "containerClassName"
    >,
) => {
    const { skillId = null } = useParams<{ skillId: string }>();
    const { data: skill } = useAgentSkill(skillId);
    const breadcrumbs = useMemo<Breadcrumb[]>(
        () => [
            { label: "Automation" },
            { label: "Skills", to: SKILLS_PATH },
            ...(skill ? [{ label: skill.title }] : []),
        ],
        [skill],
    );

    return (
        <MainInterface
            {...props}
            breadcrumbs={breadcrumbs}
            contained
            containerClassName={styles.main}
        >
            <AgentSkillPage />
        </MainInterface>
    );
};

export const AgentSkillPage = () => {
    const { skillId = null } = useParams<{ skillId: string }>();
    const navigate = useStateAwareNavigate();
    const { error: errorToast } = useToaster();
    // The list hands its filtered order over for the pager; a direct link has none.
    const { state: locationState } = useLocation();
    const skillIds = readSkillIdsFromState(locationState);
    useTemporarySideNavMode({ mode: "mini", rememberAs: "skillView" });
    // The last save's validator warnings. A fork lands on a new id, which
    // remounts the form, so the new form starts from them.
    const [savedWarnings, setSavedWarnings] = useState<{
        skillId: string;
        warnings: string[];
    } | null>(null);
    // Editing is per skill id, so a pager move leaves the editor until the
    // next skill's body is loaded and the form reset to it.
    const [editingSkillId, setEditingSkillId] = useState<string | null>(null);
    // A pristine form follows every new persisted revision (a restore, a chat
    // edit, a colleague's save). Once the member types, the session stays on
    // the revision it was editing until they reload or the form is pristine
    // again. The form resets once per followed revision once its body is
    // loaded; the page's own save marks its revision handled, as the form
    // resets itself then.
    const [dirtyRevision, setDirtyRevision] = useState<DirtyRevision | null>(
        null,
    );
    const handledRevisionKeyRef = useRef<string | null>(null);
    // An older version loaded into the editor as a draft; null is the current one.
    const [viewedRevisionId, setViewedRevisionId] = useState<string | null>(
        null,
    );
    // A metadata pick shows at once and locks the pickers until it lands.
    const [pendingMetadata, setPendingMetadata] =
        useState<AgentSkillMetadataPatch | null>(null);
    // The persisted body as the editor serializes it: the no-op baseline.
    const [loadedBody, setLoadedBody] = useState<{
        skillId: string;
        revisionId: string | null;
        body: string;
    } | null>(null);
    const [tab, setTab] = useState<AgentSkillPanelTab>("chat");
    const formRef = useRef<AgentSkillEditorFormHandle>(null);
    // The body editor instance, in state so the table menu follows its selection.
    const [bodyEditor, setBodyEditor] = useState<Editor | null>(null);
    const canManage = useCanPerformOrganizationAction(
        OrganizationAction.MANAGE_AGENT_SKILLS,
    );
    const { data: skill, isFetched, isError } = useAgentSkill(skillId);
    const { data: workspaces } = useOrganizationClients();
    const updateAgentSkill = useAgentSkillUpdate();
    const {
        enable,
        disable,
        restore,
        requestDelete,
        requestReset,
        deleteModal,
        resetModal,
    } = useAgentSkillLifecycle({
        onDeleted: () => navigate(SKILLS_PATH),
        onReset: (global) => navigate(agentSkillPagePath(global.id)),
    });
    const { defaultLayout, onLayoutChanged } = useDefaultLayout({
        id: "agent-skill-panel-group",
        storage: localStorage,
        onlySaveAfterUserInteractions: true,
    });
    const paneRef = usePanelRef();
    const [isPaneCollapsed, setIsPaneCollapsed] = useState(false);
    const hasOpenModal = useHasOpenModal();
    const isEditorOpen = skillId !== null && editingSkillId === skillId;
    const currentRevisionId = skill?.currentRevisionId ?? null;
    const isFormDirty =
        dirtyRevision !== null && dirtyRevision.skillId === skillId;
    const editorRevisionId = isFormDirty
        ? dirtyRevision.revisionId
        : currentRevisionId;
    const revisionKey = `${skillId}:${editorRevisionId}`;
    const normalizedBody =
        loadedBody !== null &&
        loadedBody.skillId === skillId &&
        loadedBody.revisionId === editorRevisionId
            ? loadedBody.body
            : "";
    const isSkillStale = editorRevisionId !== currentRevisionId;
    const hasUnsavedChanges = isEditorOpen && isFormDirty;
    const isEscapeActive = !hasOpenModal && !hasUnsavedChanges;
    const closePage = () => navigate(SKILLS_PATH);
    useKeyboardCommand({
        keys: "Escape",
        callback: closePage,
        options: {
            enabled: isEscapeActive,
            preventDefault: isEscapeActive,
            ignoreEventWhen: hasOpenMenu,
            description: "Close skill",
        },
    });
    const pager = getSkillPagerState({ skillIds, skillId, hasUnsavedChanges });
    // Paging replaces the entry so Back still returns to the list.
    const goToSkillAt = (direction: "previous" | "next") => {
        const target =
            skillIds[pager.currentIndex - 1 + (direction === "next" ? 1 : -1)];
        if (target !== undefined) {
            navigate(agentSkillPagePath(target), {
                replace: true,
                state: { skillIds },
            });
        }
    };
    const togglePane = () => {
        const pane = paneRef.current;
        if (pane?.isCollapsed()) {
            pane.expand();
        } else {
            pane?.collapse();
        }
    };
    // A collapsed pane is not a layout to come back to.
    const saveExpandedLayout = (layout: Layout, meta: LayoutChangedMeta) => {
        if ((layout[PANE_ID] ?? 0) > 0) {
            onLayoutChanged(layout, meta);
        }
    };
    // Idempotent, so the body field's sync effect can report the same text
    // again without a re-render.
    const handleBodyLoaded = (body: string) => {
        if (skillId !== null) {
            setLoadedBody((current) =>
                current?.skillId === skillId &&
                current.revisionId === editorRevisionId &&
                current.body === body
                    ? current
                    : { skillId, revisionId: editorRevisionId, body },
            );
        }
    };
    const handleDirtyChange = (isDirty: boolean) => {
        if (skillId !== null) {
            setDirtyRevision((current) =>
                pinDirtyRevision(current, {
                    isDirty,
                    skillId,
                    revisionId: currentRevisionId,
                }),
            );
        }
    };
    // Opening waits for the editor's serialization, so the form starts from
    // the same text as the no-op baseline.
    useEffect(() => {
        if (
            canManage &&
            normalizedBody !== "" &&
            skill &&
            handledRevisionKeyRef.current !== revisionKey
        ) {
            handledRevisionKeyRef.current = revisionKey;
            formRef.current?.reset({
                ...getEditAgentSkillForm(skill),
                body: normalizedBody,
            });
            setViewedRevisionId(null);
            setEditingSkillId(skill.id);
        }
    }, [canManage, normalizedBody, skill, revisionKey]);
    if (!isFetched) {
        return <Loader />;
    }
    if (isError || !skill || skill.isArchived) {
        return (
            <EmptyState
                header="Skill not found"
                ctaText="Back to skills"
                action={() => navigate(SKILLS_PATH)}
            />
        );
    }

    const clientName = skill.workspaceId
        ? (workspaces?.find(
              (workspace) => String(workspace.id) === skill.workspaceId,
          )?.name ?? "Client")
        : null;
    const target = { skillId: skill.id, title: skill.title };

    // Save keeps editing: the text just saved becomes the no-op baseline and
    // the validator's warnings stay in the form. The first edit of a Kick
    // default forks the firm's override: the saved row has a new id, so the
    // page moves to it. The page form has no Category or Frequency controls,
    // so those come from the current skill, not a revision the form pinned.
    const handleEdit = async (values: AgentSkillFormValues) => {
        const updated = await updateAgentSkill({
            skill,
            values: {
                ...values,
                category: skill.category,
                loadMode: skill.loadMode,
            },
            normalizedBody,
        });
        if (updated) {
            handledRevisionKeyRef.current = `${updated.id}:${updated.currentRevisionId}`;
            setEditingSkillId(updated.id);
            setLoadedBody({
                skillId: updated.id,
                revisionId: updated.currentRevisionId,
                body: values.body,
            });
            setSavedWarnings({
                skillId: updated.id,
                warnings: updated.warnings,
            });
            if (updated.id !== skill.id) {
                navigate(agentSkillPagePath(updated.id));
            }
            return updated.warnings;
        }
    };
    const savedValues = () => ({
        ...getEditAgentSkillForm(skill),
        body: normalizedBody,
    });
    // The body is the diff baseline itself, so a metadata pick never sends it.
    const saveMetadata = async (patch: AgentSkillMetadataPatch) => {
        setPendingMetadata(patch);
        try {
            await updateAgentSkill({
                skill,
                values: { ...savedValues(), ...patch },
                normalizedBody,
            });
        } catch (error) {
            errorToast(
                error instanceof Error
                    ? getAgentSkillErrorMessage(error, SAVE_FAILURE_MESSAGE)
                    : SAVE_FAILURE_MESSAGE,
            );
        }
        setPendingMetadata(null);
    };
    // A draft (a rejected chat edit, an older version) sits on top of the
    // saved baseline as unsaved changes, in both the form and the document.
    const loadDraft = (draft: Partial<AgentSkillFormValues>) => {
        const values = { ...savedValues(), ...draft };
        formRef.current?.reset(savedValues());
        formRef.current?.setValues(values);
        bodyEditor?.commands.setContent(values.body, {
            contentType: "markdown",
            emitUpdate: false,
        });
    };
    const openEditor = (draft: string) => {
        loadDraft({ body: draft });
        setSavedWarnings(null);
        setEditingSkillId(skill.id);
    };
    // Cancel keeps editing: the form is back at its baseline already, so the
    // document follows and the version picker returns to the current one.
    const revertEditing = () => {
        bodyEditor?.commands.setContent(normalizedBody, {
            contentType: "markdown",
            emitUpdate: false,
        });
        setViewedRevisionId(null);
    };
    const viewRevision = (revision: AgentSkillRevision) => {
        if (revision.id === skill.currentRevisionId) {
            formRef.current?.reset(savedValues());
            revertEditing();
            return;
        }
        loadDraft({
            title: revision.title,
            description: revision.description,
            body: revision.body,
        });
        setViewedRevisionId(revision.id);
    };

    const lifecycle = {
        skill,
        canManage,
        onReset: () => requestReset(target),
        onEnable: () => void enable(target),
        onDisable: () => void disable(target),
        onDelete: () => requestDelete(target),
    };

    return (
        <PageSection className={styles.page} noPaddingInline>
            <div className={styles.titleBar}>
                <div className={styles.titleBarStart}>
                    <BreakableText
                        text={skill.title}
                        className={styles.title}
                        showTooltip
                    />
                    {clientName !== null && (
                        <Tag color="neutral" size="sm" outline>
                            {clientName}
                        </Tag>
                    )}
                </div>
                <HStack gap={16} align="center" className={styles.titleBarEnd}>
                    {pager.hasSkillInList && pager.total > 1 && (
                        <>
                            <TaskPager
                                itemLabel="skill"
                                currentIndex={pager.currentIndex}
                                totalTasks={pager.total}
                                canGoToPreviousTask={pager.canGoToPrevious}
                                canGoToNextTask={pager.canGoToNext}
                                goToPreviousTask={() => goToSkillAt("previous")}
                                goToNextTask={() => goToSkillAt("next")}
                            />
                            <Separator
                                orientation="vertical"
                                className={styles.titleBarSeparator}
                            />
                        </>
                    )}
                    <HStack gap={8} align="center">
                        <TaskPaneToggleButton
                            isPaneCollapsed={isPaneCollapsed}
                            hasUnreadMessages={false}
                            onClick={togglePane}
                        />
                        <TaskCloseButton onClick={closePage} />
                    </HStack>
                </HStack>
            </div>
            <ResizablePanelGroup
                orientation="horizontal"
                defaultLayout={defaultLayout}
                onLayoutChanged={saveExpandedLayout}
                className={styles.panelGroup}
            >
                <ResizablePanel id="agent-skill-body">
                    {/* One document in both modes; edit mode only turns the
                        controls editable, so nothing moves on "Update text". */}
                    <AgentSkillEditorForm
                        key={skill.id}
                        formRef={formRef}
                        className={styles.document}
                        submitLabel="Save"
                        buttonSize="md"
                        initialValues={getEditAgentSkillForm(skill)}
                        initialWarnings={
                            savedWarnings?.skillId === skill.id
                                ? savedWarnings.warnings
                                : undefined
                        }
                        kind={skill.kind}
                        onSubmit={handleEdit}
                        onClose={revertEditing}
                        onSaved={() => setViewedRevisionId(null)}
                        onDirtyChange={handleDirtyChange}
                        renderActions={(actions) => (
                            <CommandIsland
                                sticky
                                className={styles.documentIsland}
                            >
                                <div
                                    key={isEditorOpen ? "edit" : "read"}
                                    className={styles.islandActions}
                                >
                                    {isEditorOpen ? (
                                        <AgentSkillEditActions
                                            editor={bodyEditor}
                                            lifecycle={lifecycle}
                                            currentRevisionId={
                                                skill.currentRevisionId
                                            }
                                            viewedRevisionId={viewedRevisionId}
                                            onViewRevision={viewRevision}
                                        >
                                            {actions}
                                        </AgentSkillEditActions>
                                    ) : (
                                        <AgentSkillReadActions
                                            {...lifecycle}
                                            onShowVersions={() =>
                                                setTab("versions")
                                            }
                                        />
                                    )}
                                </div>
                            </CommandIsland>
                        )}
                    >
                        <AgentSkillDocumentHeader
                            skill={skill}
                            isEditorOpen={isEditorOpen}
                            canManage={canManage}
                            defaultSkillPath={
                                skill.defaultSkillId
                                    ? agentSkillPagePath(skill.defaultSkillId)
                                    : null
                            }
                            onReset={() => requestReset(target)}
                            onReloadLatest={
                                isSkillStale
                                    ? () =>
                                          setDirtyRevision({
                                              skillId: skill.id,
                                              revisionId:
                                                  skill.currentRevisionId,
                                          })
                                    : undefined
                            }
                        />
                        <AgentSkillBodyField
                            key={skill.id}
                            markdown={skill.body}
                            revisionId={editorRevisionId}
                            isEditable={isEditorOpen}
                            kind={skill.kind}
                            onLoaded={handleBodyLoaded}
                            editorRef={setBodyEditor}
                        />
                    </AgentSkillEditorForm>
                </ResizablePanel>
                <ResizableHandle />
                <ResizablePanel
                    id={PANE_ID}
                    defaultSize="380px"
                    minSize={PANE_MIN_SIZE}
                    maxSize="45%"
                    collapsible
                    groupResizeBehavior="preserve-pixel-size"
                    panelRef={paneRef}
                    style={{ overflow: "hidden" }}
                    onResize={() =>
                        setIsPaneCollapsed(
                            paneRef.current?.isCollapsed() ?? false,
                        )
                    }
                >
                    <div className={styles.paneContent}>
                        <AgentSkillSidePanel
                            skill={skill}
                            tab={tab}
                            onTabChange={setTab}
                            canManage={canManage}
                            hasUnsavedChanges={hasUnsavedChanges}
                            pendingMetadata={pendingMetadata}
                            clientName={clientName}
                            restore={restore}
                            onUseDraft={openEditor}
                            onMetadataChange={saveMetadata}
                            onViewRevision={
                                canManage ? viewRevision : undefined
                            }
                            onMoved={(id) => navigate(agentSkillPagePath(id))}
                        />
                    </div>
                </ResizablePanel>
            </ResizablePanelGroup>

            {deleteModal}
            {resetModal}
        </PageSection>
    );
};
